import { useMemo } from 'react';
import { FormApi } from 'final-form';

import {
  Offer_Presentation_Priority,
  Offer_Presentation_Type,
  OfferFragment,
  ReceiptContentInput,
  useEarningMechanismDeleteMutation,
} from '@occc/graphql';
import {
  useMarketingContentCreateMutation,
  useMarketingContentUpdateMutation,
} from '@occc-app/dataAccessLayer/marketingContent/mutations';
import { useOfferUpdateMutation } from '@occc-app/dataAccessLayer/offer/mutations';
import {
  getCustomDataFromFormValues,
  getMarketingContentDirtyFieldsValues,
  isFieldDirty,
} from '@occc-app/offerHelpers/utils';
import { isHiddenUntilProgressEnabled } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { useEarningMechanismCreateMutation } from '@occc-app/dataAccessLayer/earningMechanism/mutations';
import { earningMechanismDuplicationService } from '@occc-app/offerDuplicationService/earningMechanismDuplicationService';
import { useIsUserPermittedTo } from '@occc/utils-access-control';

import { DesignFormProps, DesignFormPropsHookArgs, MarketingContentFormValues } from './types';
import { useOfferDisplayCategoriesCreateOrUpdateMutation } from './useOfferDisplayCategoriesCreateOrUpdateMutation';
import { validateDesignForm } from './validation';

import { GetFormPropsHook } from '../types';
import { mapFormTypeToPermission } from '../utils/offerPermission';
import { transformCustomData } from '../../routes/CampaignComponents/components/CustomData/utils';

// TODO: this file can be refactored (we could replace functions by operations from offerHelpers operations)

const getInitialValues = (offer: OfferFragment | null | undefined) => {
  const marketingContent = offer?.marketingContents?.[0];
  const presentationType = offer?.presentationType;
  const presentationPriority = offer?.presentationPriority;
  const displayCategories = offer?.displayCategories;
  const displayForOrderAhead = offer?.displayForOrderAhead ?? false;
  const fundingSource = offer?.fundingSource;
  const fundingSourceName = offer?.fundingSourceName || null;
  const trackingId = offer?.trackingConfig?.trackingId;
  const includeActivityLogsInAltriaDDM = offer?.trackingConfig?.isActivityLogIncludedInAltriaDDM;
  const hiddenUntilProgress =
    isHiddenUntilProgressEnabled(offer?.earningMechanism) &&
    offer.earningMechanism?.hiddenUntilProgress;
  const category = offer?.category ?? null;

  return {
    offer: {
      presentationType,
      presentationPriority,
      displayForOrderAhead,
      fundingSource,
      fundingSourceName,
      trackingId: trackingId ?? null,
      includeActivityLogsInAltriaDDM: includeActivityLogsInAltriaDDM ?? null,
      category,
    },
    displayCategories,
    marketingContent: marketingContent?.id
      ? {
          id: marketingContent.id,
          title: marketingContent.title || undefined,
          shortDescription: marketingContent.shortDescription || undefined,
          longDescription: marketingContent.longDescription || undefined,
          imageUrl: marketingContent.imageUrl || undefined,
          terms: marketingContent.terms || undefined,
          termsUrl: marketingContent.termsUrl || undefined,
          disclaimer: marketingContent.disclaimer || undefined,
          disclaimerUrl: marketingContent.disclaimerUrl || undefined,
          externalUrl: marketingContent.externalUrl || undefined,
          surgeonGeneralsWarning: marketingContent.surgeonGeneralsWarning || undefined,
        }
      : {},
    receiptContent: marketingContent?.receiptContent
      ? {
          shortDescription: marketingContent.receiptContent.shortDescription || undefined,
          longDescription: marketingContent.receiptContent.longDescription || undefined,
        }
      : undefined,
    customData: transformCustomData(marketingContent?.customData),
    earningMechanism: {
      hiddenUntilProgress,
    },
  };
};

const getReceiptContentInput = (
  receiptContent: MarketingContentFormValues['receiptContent'],
): ReceiptContentInput => ({
  shortDescription: receiptContent?.shortDescription || null,
  longDescription: receiptContent?.longDescription || null,
});

export const useGetDesignFormProps: GetFormPropsHook<DesignFormPropsHookArgs, DesignFormProps> = ({
  offerId,
  offer,
  type,
  onFormSubmitted,
  offerLoading,
  onError,
  onCancel,
  currentFlowType,
  orderAheadEnabled = false,
}) => {
  const [marketingContentCreate, { loading: createLoading }] = useMarketingContentCreateMutation({
    onCompleted: onFormSubmitted,
    onError,
  });
  const [marketingContentUpdate, { loading: updateLoading }] = useMarketingContentUpdateMutation({
    onCompleted: onFormSubmitted,
    onError,
  });
  const [offerUpdate, { loading: offerUpdateLoading }] = useOfferUpdateMutation({
    onCompleted: onFormSubmitted,
    onError,
  });

  const [earningMechanismCreate, { loading: earningMechanismCreateLoading }] =
    useEarningMechanismCreateMutation({
      onCompleted: onFormSubmitted,
      onError,
    });

  const [earningMechanismDelete, { loading: earningMechanismDeleteLoading }] =
    useEarningMechanismDeleteMutation({
      onCompleted: onFormSubmitted,
      onError,
    });

  const [displayCategoriesCreateOrUpdate, displayCategoriesCreateOrUpdateLoading] =
    useOfferDisplayCategoriesCreateOrUpdateMutation(onFormSubmitted, onError);

  const isFormEditable = useIsUserPermittedTo(mapFormTypeToPermission[type]);

  const onSubmit = async (
    values: MarketingContentFormValues,
    form: FormApi<MarketingContentFormValues>,
  ) => {
    const { dirtyFields } = form.getState();

    const isMarketingContentDirty = isFieldDirty<MarketingContentFormValues>(
      dirtyFields,
      'marketingContent',
    );
    const isOfferContentDirty = isFieldDirty<MarketingContentFormValues>(dirtyFields, 'offer');
    const isDisplayCategoriesDirty = isFieldDirty<MarketingContentFormValues>(
      dirtyFields,
      'displayCategories',
    );
    const isCustomDataFieldDirty = isFieldDirty<MarketingContentFormValues>(
      dirtyFields,
      'customData',
    );
    const isEarningMechanismDirty = isFieldDirty<MarketingContentFormValues>(
      dirtyFields,
      'earningMechanism.hiddenUntilProgress',
    );
    const isReceiptContentDirty = isFieldDirty<MarketingContentFormValues>(
      dirtyFields,
      'receiptContent',
    );

    const isMarketingContentCreated = !!values.marketingContent.id;

    if (
      !isMarketingContentDirty &&
      !isOfferContentDirty &&
      !isDisplayCategoriesDirty &&
      !isCustomDataFieldDirty &&
      !isEarningMechanismDirty &&
      !isReceiptContentDirty
    ) {
      onFormSubmitted();
    }

    if (
      isEarningMechanismDirty &&
      offer?.earningMechanism?.id &&
      isHiddenUntilProgressEnabled(offer?.earningMechanism)
    ) {
      await earningMechanismDelete({
        variables: {
          id: offer?.earningMechanism?.id,
        },
      });

      const configuration = earningMechanismDuplicationService.copy({
        ...offer?.earningMechanism,
        hiddenUntilProgress: values.earningMechanism?.hiddenUntilProgress || false,
      });

      await earningMechanismCreate({
        variables: {
          input: {
            offerId,
            configuration,
          },
        },
      });
    }

    if (isOfferContentDirty) {
      await offerUpdate({
        variables: {
          input: {
            id: offerId,
            presentationType: values.offer.presentationType,
            presentationPriority: values.offer.presentationPriority,
            displayForOrderAhead: orderAheadEnabled
              ? values.offer.displayForOrderAhead ?? false
              : undefined,
            fundingSource: values.offer.fundingSource,
            fundingSourceName: values.offer.fundingSourceName || null,
            category: values.offer.category?.trim() || null,
            trackingConfig: (() => {
              const trackingId = values.offer.trackingId?.trim() || null;
              return trackingId
                ? {
                    trackingId,
                    isActivityLogIncludedInAltriaDDM:
                      values.offer.includeActivityLogsInAltriaDDM ?? false,
                  }
                : null;
            })(),
          },
        },
      });
    }

    /**
     * Display categories only apply to a Tile offer prioritised by Category. If the offer
     * is changed to Featured, Banner or Hidden it can no longer have display categories
     * associated with it, so any previously associated categories are reset.
     */
    const isNewPresentationPriorityFeatured =
      offer?.presentationPriority !== Offer_Presentation_Priority.Featured &&
      values.offer.presentationPriority === Offer_Presentation_Priority.Featured;
    const isNewPresentationTypeBanner =
      offer?.presentationType !== Offer_Presentation_Type.Banner &&
      values.offer.presentationType === Offer_Presentation_Type.Banner;
    const isNewPresentationTypeHidden =
      offer?.presentationType !== Offer_Presentation_Type.Hidden &&
      values.offer.presentationType === Offer_Presentation_Type.Hidden;
    const hasCategoriesToReset =
      !!offer?.displayCategories?.length || !!values.displayCategories?.length;
    const shouldResetDisplayCategories =
      (isNewPresentationPriorityFeatured ||
        isNewPresentationTypeBanner ||
        isNewPresentationTypeHidden) &&
      hasCategoriesToReset;

    if (isDisplayCategoriesDirty || shouldResetDisplayCategories) {
      const displayCategoriesIds = shouldResetDisplayCategories
        ? []
        : values.displayCategories.map((displayCategory) => displayCategory.id);

      await displayCategoriesCreateOrUpdate({
        offerId,
        displayCategoriesIds,
      });
    }

    // TODO: marketingContentCreate possibly can be replaced by operation from offerHelper operations

    if (!isMarketingContentCreated) {
      await marketingContentCreate({
        variables: {
          input: {
            offerId,
            customData: getCustomDataFromFormValues(values.customData),
            ...values?.marketingContent,
            receiptContent: getReceiptContentInput(values.receiptContent),
            locale: 'en_US',
          },
        },
      });
      return;
    }

    if (isMarketingContentDirty || isCustomDataFieldDirty || isReceiptContentDirty) {
      const marketingContentValues = getMarketingContentDirtyFieldsValues({ dirtyFields, values });
      await marketingContentUpdate({
        variables: {
          input: {
            id: values.marketingContent.id!,
            customData: getCustomDataFromFormValues(values.customData),
            ...marketingContentValues,
            ...(isReceiptContentDirty
              ? { receiptContent: getReceiptContentInput(values.receiptContent) }
              : {}),
          },
        },
      });
    }
  };

  const initialValues = useMemo(() => getInitialValues(offer), [offer]);

  return {
    onSubmit,
    validate: validateDesignForm,
    isFormEditable,
    initialValues,
    loading:
      offerLoading ||
      createLoading ||
      updateLoading ||
      offerUpdateLoading ||
      displayCategoriesCreateOrUpdateLoading ||
      earningMechanismDeleteLoading ||
      earningMechanismCreateLoading,
    onCancelClick: onCancel,
    currentFlowType,
    hiddenUntilProgressIsEnabled: isHiddenUntilProgressEnabled(offer?.earningMechanism),
  };
};
